import { useState, useEffect, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import {
    MessageSquare, Trash2, CheckCircle, AlertCircle,
    WifiOff, Loader2, Settings2, Plus,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { WA_ACCOUNTS_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

// ── Status badge ───────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
    if (status === 'connected') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="h-3 w-3" />Connected
        </span>
    );
    if (status === 'error') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <AlertCircle className="h-3 w-3" />Error
        </span>
    );
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            <WifiOff className="h-3 w-3" />Disconnected
        </span>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────

const EMPTY_CONNECT = { display_name: '', wati_api_endpoint: '', wati_api_key: '' };
const EMPTY_EDIT    = { display_name: '', wati_api_endpoint: '', wati_api_key: '' };

export default function WhatsAppIntegration() {
    const { fetch, create, update, remove } = useApiMethods();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);

    // Connect modal
    const [connectOpen, setConnectOpen] = useState(false);
    const [connectForm, setConnectForm] = useState(EMPTY_CONNECT);
    const [connecting, setConnecting] = useState(false);

    // Configure modal
    const [configOpen, setConfigOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_EDIT);
    const [configSaving, setConfigSaving] = useState(false);

    const [deletingId, setDeletingId] = useState(null);

    const loadAccounts = useCallback(async () => {
        setLoading(true);
        const res = await fetch(WA_ACCOUNTS_URL);
        if (res?.status === 'success') setAccounts(res.data || []);
        setLoading(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadAccounts(); }, []);

    // ── Connect new account ───────────────────────────────────────────────────

    const handleConnect = async () => {
        if (!connectForm.wati_api_endpoint || !connectForm.wati_api_key) {
            ToastMsg('error', 'WATI Endpoint and API Key are required');
            return;
        }
        setConnecting(true);
        const res = await create(WA_ACCOUNTS_URL, connectForm);
        setConnecting(false);
        if (res?.status === 'success') {
            ToastMsg('success', 'WhatsApp account connected via WATI');
            setConnectOpen(false);
            setConnectForm(EMPTY_CONNECT);
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Connection failed');
        }
    };

    // ── Configure existing account ────────────────────────────────────────────

    const openConfig = (acc) => {
        setEditingAccount(acc);
        setEditForm({
            display_name: acc.display_name,
            wati_api_endpoint: acc.wati_api_endpoint,
            wati_api_key: '',
        });
        setConfigOpen(true);
    };

    const handleConfig = async () => {
        setConfigSaving(true);
        const payload = { id: editingAccount.id, ...editForm };
        if (!payload.wati_api_key) delete payload.wati_api_key; // don't overwrite if blank
        const res = await update(WA_ACCOUNTS_URL, payload);
        setConfigSaving(false);
        if (res?.status === 'success') {
            ToastMsg('success', 'Account updated');
            setConfigOpen(false);
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Update failed');
        }
    };

    const handleDisconnect = async (id) => {
        setDeletingId(id);
        const res = await remove(`${WA_ACCOUNTS_URL}?id=${id}`);
        setDeletingId(null);
        if (res?.status === 'success') {
            ToastMsg('success', 'Account disconnected');
            loadAccounts();
        } else {
            ToastMsg('error', res?.message || 'Failed to disconnect');
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">WhatsApp Integration</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Connect your WhatsApp Business Account via WATI
                        </p>
                    </div>
                    <Button
                        onClick={() => { setConnectForm(EMPTY_CONNECT); setConnectOpen(true); }}
                        className="flex items-center gap-2 text-sm"
                    >
                        <Plus className="h-4 w-4" />Connect Account
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/20 dark:to-green-800/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                            <MessageSquare className="h-6 w-6 text-green-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No accounts connected</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Click "Connect Account" to link your WATI WhatsApp Business Account
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {accounts.map(acc => (
                            <div key={acc.id} className="p-4 rounded-xl border-2 border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="min-w-0 flex-1">
                                        <span className="text-xs font-bold uppercase tracking-wider text-green-600 dark:text-green-400">
                                            WhatsApp via WATI
                                        </span>
                                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mt-0.5 truncate">
                                            {acc.display_name || 'Unnamed Account'}
                                        </p>
                                        {acc.phone_number && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{acc.phone_number}</p>
                                        )}
                                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate" title={acc.wati_api_endpoint}>
                                            {acc.wati_api_endpoint}
                                        </p>
                                    </div>
                                    <div className="ml-2 flex-shrink-0">
                                        <StatusBadge status={acc.status} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 pt-2 border-t border-green-200 dark:border-green-800">
                                    <button
                                        onClick={() => openConfig(acc)}
                                        className="flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 font-medium"
                                    >
                                        <Settings2 className="h-3 w-3" />Configure
                                    </button>
                                    <button
                                        onClick={() => handleDisconnect(acc.id)}
                                        disabled={deletingId === acc.id}
                                        className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-700 dark:hover:text-red-400 font-medium"
                                    >
                                        {deletingId === acc.id
                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                            : <Trash2 className="h-3 w-3" />}
                                        Disconnect
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </ContainerCard>

            {/* Connect Modal */}
            <DialogModal
                open={connectOpen}
                onClose={() => setConnectOpen(false)}
                title="Connect WATI Account"
                width="480px"
                showActions={false}
            >
                <div className="space-y-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Enter your WATI API endpoint and Bearer token. You can find these in your{' '}
                        <strong>WATI Dashboard → API</strong>.
                    </p>
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                            Display Name
                        </label>
                        <Input
                            value={connectForm.display_name}
                            onChange={e => setConnectForm(p => ({ ...p, display_name: e.target.value }))}
                            placeholder="e.g. My Business WA"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                            WATI API Endpoint <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={connectForm.wati_api_endpoint}
                            onChange={e => setConnectForm(p => ({ ...p, wati_api_endpoint: e.target.value }))}
                            placeholder="e.g. https://live-mt-server.wati.io/123456"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                            API Key (Bearer Token) <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="password"
                            value={connectForm.wati_api_key}
                            onChange={e => setConnectForm(p => ({ ...p, wati_api_key: e.target.value }))}
                            placeholder="Paste your WATI API key"
                        />
                    </div>
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" onClick={() => setConnectOpen(false)} className="flex-1 text-sm">Cancel</Button>
                        <Button
                            onClick={handleConnect}
                            disabled={connecting || !connectForm.wati_api_endpoint || !connectForm.wati_api_key}
                            className="flex-1 text-sm flex items-center gap-2 justify-center"
                        >
                            {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
                            {connecting ? 'Testing…' : 'Connect'}
                        </Button>
                    </div>
                </div>
            </DialogModal>

            {/* Configure Modal */}
            <DialogModal
                open={configOpen}
                onClose={() => setConfigOpen(false)}
                title="Configure Account"
                width="480px"
                showActions={false}
            >
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Display Name</label>
                        <Input
                            value={editForm.display_name}
                            onChange={e => setEditForm(p => ({ ...p, display_name: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">WATI API Endpoint</label>
                        <Input
                            value={editForm.wati_api_endpoint}
                            onChange={e => setEditForm(p => ({ ...p, wati_api_endpoint: e.target.value }))}
                            placeholder="https://live-mt-server.wati.io/123456"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                            New API Key <span className="text-gray-400 text-[10px]">(leave blank to keep current)</span>
                        </label>
                        <Input
                            type="password"
                            value={editForm.wati_api_key}
                            onChange={e => setEditForm(p => ({ ...p, wati_api_key: e.target.value }))}
                            placeholder="Enter new key to update"
                        />
                    </div>
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" onClick={() => setConfigOpen(false)} className="flex-1 text-sm">Cancel</Button>
                        <Button onClick={handleConfig} disabled={configSaving} className="flex-1 text-sm flex items-center gap-2 justify-center">
                            {configSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Save
                        </Button>
                    </div>
                </div>
            </DialogModal>
        </div>
    );
}
